import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LogIn as LoginIcon,
  Menu as MenuIcon,
  X as CloseIcon,
  ChevronDown as ChevronDownIcon,
  Camera as CameraIcon,
  Users as UsersIcon,
  Clock as ClockIcon
} from 'lucide-react';
import './Home.css';

const Home = ({ isAuthenticated, userInfo }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const getInitials = () => {
    if (!userInfo) return '?';
    const firstInitial = userInfo.firstName ? userInfo.firstName[0] : '';
    const lastInitial = userInfo.lastName ? userInfo.lastName[0] : '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase();
    return initials || userInfo.email[0]?.toUpperCase() || '?';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="container">
          <div className="header-content">
            <h1 className="logo">PlayMaker Pics</h1>
            
            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <ul className="nav-links">
                <li><Link to="/" className="nav-link active">Home</Link></li>
                <li><Link to="/about" className="nav-link">About</Link></li>
                <li><Link to="/pricing" className="nav-link">Pricing</Link></li>
                <li><Link to="/contact" className="nav-link">Contact</Link></li>
              </ul>
            </nav>
            
            <div className="header-buttons">
              {isAuthenticated ? (
                <div className="user-menu">
                  <Link to="/dashboard" className="dashboard-btn">Dashboard</Link>
                  <div className="user-avatar">
                    {getInitials()}
                  </div>
                </div>
              ) : (
                <Link to="/login" className="login-btn">
                  <LoginIcon size={16} className="mr-2" />
                  Login
                </Link>
              )}
              
              {/* Mobile menu button */}
              <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                {mobileMenuOpen ? 
                  <CloseIcon size={24} /> : 
                  <MenuIcon size={24} />
                }
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-links">
            <li><Link to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>Home</Link></li>
            <li><Link to="/about" className="mobile-nav-link" onClick={toggleMobileMenu}>About</Link></li>
            <li><Link to="/pricing" className="mobile-nav-link" onClick={toggleMobileMenu}>Pricing</Link></li>
            <li><Link to="/contact" className="mobile-nav-link" onClick={toggleMobileMenu}>Contact</Link></li>
            {isAuthenticated && (
              <li><Link to="/dashboard" className="mobile-nav-link" onClick={toggleMobileMenu}>Dashboard</Link></li>
            )}
            {!isAuthenticated && (
              <li><Link to="/login" className="mobile-nav-link login" onClick={toggleMobileMenu}>Login</Link></li>
            )}
          </ul>
        </div>
      </header>

      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h2 className="hero-title">Professional Sports Photography</h2>
            <p className="hero-subtitle">
              Capture perfect moments with our AI-powered tools and templates
            </p>
            <div className="hero-buttons">
              {isAuthenticated ? (
                <Link to="/dashboard" className="primary-btn">Go to Dashboard</Link>
              ) : (
                <Link to="/register" className="primary-btn">Get Started</Link>
              )}
              <Link to="/editor" className="secondary-btn">Try Editor</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose PlayMaker Pics?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <CameraIcon size={32} />
              </div>
              <h3>Professional Quality</h3>
              <p>Create stunning sports photos with our AI-powered tools and professional templates.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <UsersIcon size={32} />
              </div>
              <h3>Team Management</h3>
              <p>Easily organize photos by teams, events, and players with our intuitive dashboard.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <ClockIcon size={32} />
              </div>
              <h3>Time Saving</h3>
              <p>Reduce editing time by up to 80% with automated background removal and templates.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to transform your sports photography?</h2>
            <p>Join thousands of photographers who have elevated their game with PlayMaker Pics.</p>
            <Link to="/register" className="cta-button">Start Free Trial</Link>
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-logo">PlayMaker Pics</h3>
              <p>Professional sports photography made easy.</p>
            </div>
            <div className="footer-links">
              <div className="footer-links-column">
                <h4>Company</h4>
                <ul>
                  <li><Link to="/about">About Us</Link></li>
                  <li><Link to="/contact">Contact</Link></li>
                  <li><Link to="/pricing">Pricing</Link></li>
                </ul>
              </div>
              <div className="footer-links-column">
                <h4>Resources</h4>
                <ul>
                  <li><Link to="/blog">Blog</Link></li>
                  <li><Link to="/tutorials">Tutorials</Link></li>
                  <li><Link to="/faq">FAQs</Link></li>
                </ul>
              </div>
              <div className="footer-links-column">
                <h4>Legal</h4>
                <ul>
                  <li><Link to="/terms">Terms of Service</Link></li>
                  <li><Link to="/privacy">Privacy Policy</Link></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} PlayMaker Pics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;